import i18n from 'i18next';
import { omit, pickBy } from 'ramda';
import { withTranslation } from 'react-i18next';
import {
  branch,
  compose,
  defaultProps,
  mapProps,
  renameProp,
  renderNothing,
  shouldUpdate,
  withHandlers,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
// import createLabel from '../createLabel';
import cx from 'classnames';
import Icon from 'components/Icon';
import Downshift from 'downshift';
import { Button as UIButton } from '../Button';

import styles from './styles.css';

const pickItemsByValue = (value = '') =>
  pickBy((v) => !v || v.toLowerCase().includes(value.toLowerCase()));

const getItems = compose(
  branch(
    ({ multiple }) => multiple,
    withPropsOnChange(
      ['selectedItems', 'items'],
      ({ items, selectedItems, ignoreSearch }) => ({
        items: ignoreSearch
          ? items
          : pickBy((_, k) => !selectedItems.includes(k))(items),
      })
    )
  ),
  branch(
    ({ searchable }) => !!searchable,
    withPropsOnChange(['value', 'items'], ({ items, value, ignoreSearch }) => ({
      items: ignoreSearch ? items : pickItemsByValue(value)(items),
    })),
    withPropsOnChange(['value', 'items'], ({ items, value, stateless }) => ({
      items:
        value && !stateless
          ? // pickBy(v => !v.toLowerCase().includes(value.toLowerCase()))(items) TODO: figure if new one is better
            pickBy(
              (v, k) =>
                (Array.isArray(value) && !value.includes(k)) || value !== k
            )(items)
          : items,
    }))
  )
);

const withState = withStateHandlers(
  ({ defaultValue, value }) => ({
    search: '',
    selected: defaultValue || value || [],
  }),
  {
    onSearch:
      (state, { onChangeSearch }) =>
      (search) => {
        if (onChangeSearch) onChangeSearch(search);
        return { ...state, search };
      },
    addSelected:
      (state, { onChange }) =>
      (item) => {
        const selected = [...state.selected, item];
        if (state.selected.includes(item)) return { ...state, search: '' };
        onChange(selected);
        return { search: '', selected };
      },
    setSelected:
      (state, { onChange }) =>
      (selected) => {
        onChange(selected);
        return { ...state, selected };
      },
  }
);

const replaceDescription = (_, match) => `<em>(${match})</em>`;

const Item = ({ text = '', highlighted, itemKey, ...itemProps }) => (
  <button
    {...itemProps}
    className={cx(styles.item, highlighted && styles.highlighted)}
    dangerouslySetInnerHTML={{
      __html: text.replace(/\((.+?)\)/g, replaceDescription),
    }}
  />
);

const Items = compose(
  branch(({ isOpen }) => !isOpen, renderNothing),
  getItems
)(
  ({ items, itemProps, highlightedIndex, selectedItem, Component }: any) =>
    !!Object.keys(items).length && (
      <div className={styles.items}>
        {Object.keys(items).map((item, index) => (
          <Component
            key={item}
            title={item}
            highlighted={highlightedIndex === index}
            text={items[item]}
            {...itemProps({ item })}
          />
        ))}
      </div>
    )
);

const Input = compose(
  withHandlers({
    onFocus:
      ({ onSearch, onFocus }) =>
      (e) => {
        if (onSearch) onSearch('');
        return onFocus(e);
      },
    onChange:
      ({ onSearch, onChange }) =>
      (e) => {
        if (onSearch) onSearch(e.target.value);
        return onChange(e);
      },
    onKeyDown:
      ({ onKeyDown, onCreate, ...props }) =>
      (e) => {
        if (
          onCreate &&
          e.key.toLowerCase() === 'enter' &&
          !props['aria-activedescendant']
        ) {
          onCreate();
        }
        return onKeyDown(e);
      },
  }),
  branch(
    ({ multiple, search }) => multiple && search !== undefined,
    renameProp('search', 'value')
  ),
  mapProps(omit(['onSearch', 'onCreate'])),
  withTranslation()
)(({ value, items, search, placeholder, defaultValue, t, ...props }: any) => (
  <input
    {...props}
    className={styles.input}
    placeholder={
      (items && items[defaultValue]) || defaultValue || t(placeholder)
    }
    value={((items && items[value]) || value || search || '')
      .replace(/·/g, '')
      .replace(/\ \((.+?)\)/g, '')}
  />
));

const Button = ({ theme, value, multiple, placeholder, ...buttonProps }) => (
  <button
    {...buttonProps}
    className={cx(styles.button, !value && styles.placeholder)}
  >
    <span className={styles.truncate}>{value || i18n.t(placeholder)}</span>
  </button>
);

export const Autocomplete = compose(
  shouldUpdate((prev: any, next: any) => {
    if (prev.disabled !== next.disabled) return true;
    if (prev.options !== next.options) return true;
    if (typeof prev.value === 'object' && prev.value !== null) {
      if (prev.value.length !== next.value.length) return true;
    } else {
      if (prev.value !== next.value) return true;
    }
    return false;
  }),
  defaultProps({
    placeholder: 'common:general.placeholder_select',
  }),
  withProps(
    ({ allowCreate }) =>
      allowCreate && {
        searchable: true,
      }
  ),
  withPropsOnChange(['options'], ({ options }) => ({
    items: Array.isArray(options)
      ? options.reduce(
          (acc, item) => ({ ...acc, [String(item)]: String(item) }),
          {}
        )
      : options || {},
  })),
  branch(
    ({ searchable, multiple, allowCreate }) =>
      searchable || multiple || allowCreate,
    withState
  ),
  branch(
    ({ searchable, multiple, allowCreate }) =>
      searchable || multiple || allowCreate,
    withPropsOnChange(
      ['value'],
      ({ value, setSelected, selected }) =>
        value && value !== selected && setSelected(value) && {}
    )
  ),
  branch(
    ({ multiple }) => multiple,
    withHandlers({
      onChange: ({ addSelected }) => addSelected,
      onCreate:
        ({ addSelected, search }) =>
        () =>
          addSelected(search),
    })
  ),
  withHandlers({
    onCreate:
      ({ onCreate, search, allowEmpty }) =>
      () =>
        search?.trim().length
          ? onCreate(search)
          : allowEmpty && onCreate(search),
  })
)(
  ({
    searchable, // allow to search inside input
    multiple, // allow to select multiple items
    disabled,
    allowCreate,
    autoFocus,
    hideArrow,

    onChange,
    onCreate,
    onSearch,

    placeholder,
    items,
    search,
    selected,
    icon,
    value,
    showAddButton,
    defaultValue,
    itemRenderer,
    ignoreSearch,
    stateless,
    getLabel,
    allowEmpty,
  }) => (
    <Downshift
      onChange={!stateless ? onChange : () => {}}
      onSelect={stateless ? onChange : () => {}}
      initialInputValue={defaultValue ? String(defaultValue) : defaultValue}
      inputValue={!searchable && !showAddButton ? value : undefined}
    >
      {({
        getInputProps,
        getItemProps,
        getToggleButtonProps,
        isOpen,
        inputValue,
        selectedItem,
        highlightedIndex,
        openMenu,
      }) => (
        <div className={cx(styles.autocomplete, isOpen && styles.focus)}>
          {icon}
          {searchable && (
            <Input
              {...getInputProps({
                defaultValue,
                placeholder,
                autoFocus,
                onFocus: openMenu,
              })}
              onSearch={onSearch}
              onCreate={allowCreate && onCreate}
              search={search}
              multiple={multiple}
              items={items}
            />
          )}
          {!searchable && (
            <Button
              {...getToggleButtonProps({ disabled, placeholder })}
              value={
                multiple || stateless
                  ? undefined
                  : getLabel
                  ? getLabel(items[inputValue] || inputValue)
                  : items[inputValue] || inputValue
              }
            />
          )}
          {!hideArrow && !!Object.keys(items).length && (
            <Icon
              name="arrow-down"
              width={24}
              height={24}
              className={styles.chevron}
            />
          )}
          {showAddButton && (
            <UIButton
              className={styles.addItemButton}
              type={
                (search && search.trim().length > 0 && 'primary') || 'secondary'
              }
              onClick={allowCreate && onCreate}
            >
              <Icon name="plus" width={14} height={14} />
            </UIButton>
          )}
          <Items
            isOpen={isOpen}
            items={items}
            ignoreSearch={ignoreSearch}
            itemProps={getItemProps}
            Component={itemRenderer || Item}
            multiple={multiple}
            selectedItems={multiple && selected}
            searchable={searchable}
            selectedItem={selectedItem}
            stateless={stateless}
            highlightedIndex={highlightedIndex}
            value={search !== undefined ? search : inputValue}
          />
        </div>
      )}
    </Downshift>
  )
);

Autocomplete.displayName = 'Autocomplete';
